import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import { categories } from "@/mocks/categories";
import { mangas } from "@/mocks/mangas";
import { articles } from "@/mocks/articles";
import { getCategoryBySlug, getMangasByCategory } from "@/utils/content";

const BASE_URL = "https://mangistart.github.io";

function CategoryCards() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {categories.map((cat) => {
        const list = getMangasByCategory(cat.slug);
        return (
          <Link
            key={cat.slug}
            to={`/category/${cat.slug}`}
            className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
          >
            <div className="relative h-40 w-full overflow-hidden">
              <img
                src={cat.image}
                alt={cat.name}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <span className="absolute bottom-2 left-3 flex items-center gap-1 text-sm font-medium text-white">
                <i className="ri-bookmark-line text-accent-300" />
                {list.length || cat.count} 部收录
              </span>
            </div>
            <div className="flex flex-1 items-center justify-between px-4 py-3">
              <div>
                <h2 className="font-medium text-foreground-900">{cat.name}</h2>
                <p className="mt-0.5 line-clamp-1 text-xs text-foreground-500">{cat.desc}</p>
              </div>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                <i className={cat.icon} />
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export default function Category() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const current = slug ? getCategoryBySlug(slug) : undefined;

  // 分类概览页
  if (!slug) {
    return (
      <>
        <Seo
          title="漫画分类大全 - 漫画类型大全、漫画题材分类导航"
          description="浏览全部漫画分类大全与漫画题材分类，涵盖热血冒险、恋爱言情、悬疑推理、玄幻修仙、科幻机甲等漫画类型大全。"
          keywords="漫画分类大全,漫画类型大全,漫画题材分类,漫画排行榜大全"
        />
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "漫画分类大全",
            url: `${BASE_URL}/category`,
            about: "漫画分类与漫画题材分类导航",
            inLanguage: "zh-CN",
          }}
        />
        <div className="flex min-h-screen flex-col bg-background-50">
          <Navbar />
          <main className="flex-1">
            <Breadcrumb items={[{ label: "漫画分类大全" }]} />
            <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
              <div className="max-w-2xl">
                <span className="text-sm font-medium text-accent-600">CATEGORY</span>
                <h1 className="mt-2 font-heading text-2xl text-foreground-900 md:text-4xl">漫画分类大全</h1>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  系统梳理漫画类型大全与漫画题材分类，覆盖热血冒险、恋爱言情、悬疑推理、玄幻修仙、科幻机甲、搞笑日常等热门题材，帮助你在漫画海洋中快速锁定想看的方向。
                </p>
              </div>
              <div className="mt-8">
                <CategoryCards />
              </div>
            </div>
          </main>
          <Footer />
          <BackToTop />
        </div>
      </>
    );
  }

  // 分类不存在
  if (!current) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-24 text-center md:px-6">
          <h1 className="font-heading text-2xl text-foreground-900">未找到该分类</h1>
          <p className="mt-3 text-foreground-600">抱歉，该漫画分类不存在或已被调整。</p>
          <button
            type="button"
            onClick={() => navigate("/category")}
            className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回分类大全
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const list = getMangasByCategory(slug);
  const relatedArticles = articles.filter((a) => a.category === slug);

  return (
    <>
      <Seo
        title={`${current.name}漫画推荐大全 - 漫画分类大全导航`}
        description={`${current.name}漫画推荐大全：精选${current.name}题材人气作品，附${current.name}漫画排行榜与人气排行，帮你找到想看的${current.name}漫画。`}
        keywords={`${current.name},漫画分类大全,漫画排行榜大全,漫画推荐大全`}
      />
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${current.name}漫画大全`,
            url: `${BASE_URL}/category/${current.slug}`,
            about: `${current.name}漫画分类`,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${BASE_URL}/` },
              { "@type": "ListItem", position: 2, name: "漫画分类大全", item: `${BASE_URL}/category` },
              { "@type": "ListItem", position: 3, name: current.name },
            ],
          },
        ]}
      />
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex-1">
          <Breadcrumb
            items={[{ label: "漫画分类大全", path: "/category" }, { label: current.name }]}
          />

          <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6">
            <div className="relative overflow-hidden rounded-lg">
              <div className="absolute inset-0">
                <img
                  src={current.image}
                  alt={current.name}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30"></div>
              </div>
              <div className="relative z-10 px-6 py-12 md:px-10 md:py-16">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-2xl text-accent-300 backdrop-blur">
                  <i className={current.icon} />
                </span>
                <h1 className="mt-4 font-heading text-2xl text-white md:text-4xl">{current.name}漫画大全</h1>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">{current.desc}</p>
                <div className="mt-5 flex flex-wrap gap-4 text-sm text-white/90">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-bookmark-3-line text-accent-300" />
                    收录 {list.length} 部作品
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-300" />
                    编辑精选推荐
                  </span>
                </div>
              </div>
            </div>

            {/* 分类切换器 */}
            <div className="mt-6 overflow-x-auto">
              <nav
                aria-label="分类切换"
                className="inline-flex flex-nowrap items-center gap-2 rounded-full border border-background-200/70 bg-background-50 p-1"
              >
                <button
                  type="button"
                  onClick={() => navigate("/category")}
                  className="whitespace-nowrap rounded-full px-4 py-2 text-sm text-foreground-600 hover:text-primary-600"
                >
                  全部
                </button>
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => navigate(`/category/${c.slug}`)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      c.slug === current.slug
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-600 hover:bg-background-100 hover:text-primary-600"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* 该分类漫画列表 */}
          <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6" aria-label={`${current.name}漫画列表`}>
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">
                {current.name}漫画推荐大全
              </h2>
              <span className="text-sm text-foreground-500">{list.length} 部作品</span>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {list.map((m) => (
                <Link
                  key={m.slug}
                  to={`/category/${current.slug}`}
                  className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-52 w-full overflow-hidden md:h-60">
                    <img
                      src={m.cover}
                      alt={m.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <span className="absolute right-2 top-2 flex items-center gap-0.5 rounded-full bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50">
                      <i className="ri-star-fill" />
                      {m.rating}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600 md:text-base">
                      {m.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">
                      {m.author} · {m.chapters} 话 · {m.status}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">{m.intro}</p>
                  </div>
                </Link>
              ))}
            </div>
            {list.length === 0 && (
              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center text-sm text-foreground-500">
                该分类暂无收录作品，敬请期待。
              </div>
            )}
          </section>

          {/* 关联文章 */}
          {relatedArticles.length > 0 && (
            <section className="mx-auto w-full max-w-7xl px-4 pb-10 md:px-6" aria-label="相关推荐文章">
              <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">相关漫画推荐指南</h2>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {relatedArticles.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="relative h-40 w-full overflow-hidden">
                      <img
                        src={a.cover}
                        alt={a.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="line-clamp-2 text-sm font-medium leading-relaxed text-foreground-900 group-hover:text-primary-600">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500">{a.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
}